import { fetchAllAnswerShareSubmissions } from "@/api/answerShare";
import { ApiError } from "@/api/client";
import { paginateSubmissions } from "@/lib/forms/submissionPagination";
import { formatApiQueryError } from "@/lib/http/formatQueryError";
import type { PaginatedMeta } from "@/types/formList";
import type { FormSubmissionDto } from "@/types/submission";
import { useMemo, useState } from "react";
import { useAsyncResource } from "./useAsyncResource";

export const ANSWER_SHARE_SUBMISSIONS_PER_PAGE = [15, 25, 50] as const;

interface UseAnswerShareSubmissionsQueryOptions {
  token: string;
  accessToken: string | null;
  page: number;
  perPage: number;
  enabled?: boolean;
}

interface UseAnswerShareSubmissionsQueryResult {
  submissions: FormSubmissionDto[];
  allSubmissions: FormSubmissionDto[];
  meta: PaginatedMeta | null;
  isLoading: boolean;
  error: string | null;
  accessExpired: boolean;
  reload: () => void;
}

export function useAnswerShareSubmissionsQuery({
  token,
  accessToken,
  page,
  perPage,
  enabled = true,
}: UseAnswerShareSubmissionsQueryOptions): UseAnswerShareSubmissionsQueryResult {
  const [accessExpired, setAccessExpired] = useState(false);

  const {
    data: allSubmissions = [],
    isLoading,
    error,
    reload,
  } = useAsyncResource({
    enabled: enabled && accessToken != null,
    deps: [token, accessToken],
    fetch: () => fetchAllAnswerShareSubmissions(token, accessToken!),
    formatError: (err) => {
      if (err instanceof ApiError && err.status === 401) {
        setAccessExpired(true);
        return "Сессия доступа истекла. Введите пароль снова.";
      }
      return formatApiQueryError(err, "Не удалось загрузить ответы", {
        statusPrefix: "Не удалось загрузить ответы",
      });
    },
    onDisabled: () => setAccessExpired(false),
    initialLoading: false,
    clearOnError: true,
  });

  const { pageItems, sorted, meta } = useMemo(
    () => paginateSubmissions(allSubmissions, page, perPage),
    [allSubmissions, page, perPage],
  );

  return {
    submissions: pageItems,
    allSubmissions: sorted,
    meta,
    isLoading,
    error,
    accessExpired,
    reload,
  };
}
